import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import moment from 'moment';
import { ButtonV2, Icon, Filter, Form, InfiniteScroll, Preloader } from 'components';
import Scrollbars from 'react-custom-scrollbars';
import get from 'lodash/get';
import activityTypes from 'constants/activityTypes';
import { getActivityIconPath } from 'lib/utils';
import Activity from './Activity';
import withLaunchDarkly from 'hocs/withLaunchDarkly';

import './style.scss';

const todayRange = () => [
  moment()
    .startOf('day')
    .format('YYYY-MM-DD'),
  moment()
    .endOf('day')
    .format('YYYY-MM-DD')
];
const weekRange = () => [
  moment()
    .startOf('isoWeek')
    .format('YYYY-MM-DD'),
  moment()
    .endOf('isoWeek')
    .endOf('day')
    .format('YYYY-MM-DD')
];

class ViewActivityObservations extends Component {
  constructor(props) {
    super(props);

    const baseFilters = get(props, 'data.filters', {});

    if (!baseFilters.date) {
      baseFilters.date = { type: 'weekly', dateRange: weekRange() };
    }

    this.state = {
      filters: baseFilters,
      activities: [],
      selected: {},
      page: 1,
      hasMore: true,
      photoUrl: null,
      observationIds: []
    };
  }

  componentDidMount() {
    const { kidId, measureId, progressId } = this.props.data;
    const { filters } = this.state;
    const useFeed = this.props.flags['activities-feed'];

    const dateFrom = filters.date?.dateRange?.[0] || todayRange()[0];
    const dateTo = filters.date?.dateRange?.[1] || todayRange()[1];

    req.students();

    const getIds = useFeed ? req.kidAssessmentObservationsV2 : req.kidAssessmentObservations;

    getIds({ id: kidId, measure_id: measureId, progress_id: progressId, start_date: dateFrom, end_date: dateTo }).then(
      activityIds => {
        if (!Array.isArray(activityIds) || !activityIds.length) {
          this.setState({ observationIds: [] }, this.loadMoreActivities);
          return;
        }

        const onSuccess = pageOne => {
          const list = useFeed ? pageOne.activities : pageOne.daily_activities;
          const selectedRows = (list || []).filter(a => activityIds.includes(a.id));

          const selectedMap = {};
          activityIds.forEach(id => {
            selectedMap[id] = true;
          });

          this.setState(
            {
              activities: selectedRows,
              observationIds: activityIds,
              selected: selectedMap,
              page: 1
            },
            () => this.loadMoreActivities()
          );
        };

        if (useFeed) {
          req
            .feedActivities({
              activities_filter: {
                date_from: dateFrom,
                date_to: dateTo,
                activity_type: filters.activity_type || undefined
              },
              kids_filter: { kid_ids: [filters.kid_id] },
              page: 1
            })
            .then(onSuccess)
            .catch(() => this.setState({ observationIds: activityIds }, () => this.loadMoreActivities()));
        } else {
          req
            .dailyActivities(
              {
                filters: {
                  daily_activity: {
                    date_from: dateFrom,
                    date_to: dateTo,
                    staff_only: filters.staff_only || undefined,
                    activity_type: filters.activity_type || undefined,
                    kid_id: filters.kid_id || undefined
                  }
                },
                page: 1
              },
              undefined,
              { checkHistory: dateTo }
            )
            .then(onSuccess)
            .catch(() => this.setState({ observationIds: activityIds }, () => this.loadMoreActivities()));
        }
      }
    );
  }

  get readOnly() {
    return this.props.data.readOnly;
  }

  @bind onPhotoView(photoUrl) {
    this.setState({ photoUrl });
  }
  @bind onPhotoViewClose() {
    this.setState({ photoUrl: false });
  }

  @bind
  selectActivity(activity) {
    this.setState(prev => ({
      selected: { ...prev.selected, [activity.id]: !prev.selected[activity.id] }
    }));
  }

  @bind
  onSubmit() {
    if (this.readOnly) {
      return this.props.onClose();
    }

    const selected = [];
    Object.keys(this.state.selected).forEach(id => {
      if (this.state.selected[id]) selected.push(id);
    });
    this.props.onClose(selected);
  }

  @bind
  loadMoreActivities() {
    const { activities, filters, page, observationIds } = this.state;
    const { total } = this.props;
    if (activities.length >= total && total && page !== 1) return;

    const dateFrom = filters.date?.dateRange?.[0] || todayRange()[0];
    const dateTo = filters.date?.dateRange?.[1] || todayRange()[1];

    if (this.props.flags['activities-feed']) {
      req
        .feedActivities({
          activities_filter: {
            date_from: dateFrom,
            date_to: dateTo,
            activity_type: filters.activity_type || undefined
          },
          kids_filter: { kid_ids: [filters.kid_id] },
          page
        })
        .then(result => {
          const newActivities = result.activities.filter(a => !this.state.activities.some(act => act.id === a.id));

          this.setState(prev => ({
            activities: [...prev.activities, ...newActivities],
            observationIds: [...prev.observationIds, ...newActivities.map(a => a.id)],
            hasMore: !!result.activities.length,
            page: prev.page + 1
          }));
        });
    } else {
      req
        .dailyActivities(
          {
            filters: {
              daily_activity: {
                date_from: dateFrom,
                date_to: dateTo,
                staff_only: filters.staff_only || undefined,
                activity_type: filters.activity_type || undefined,
                kid_id: filters.kid_id || undefined
              }
            },
            page
          },
          undefined,
          { checkHistory: dateTo }
        )
        .then(result => {
          const newActivities = result.daily_activities.filter(
            a => !this.state.activities.some(act => act.id === a.id)
          );

          this.setState(prev => ({
            activities: [...prev.activities, ...newActivities],
            observationIds: [...prev.observationIds, ...newActivities.map(a => a.id)],
            hasMore: !!result.daily_activities.length,
            page: prev.page + 1
          }));
        });
    }
  }

  @bind
  updateFilters(values) {
    const updated = { ...this.state.filters, ...values };

    if (values.date && values.date.type === 'daily' && !values.date.dateRange?.length) {
      updated.date = { type: 'daily', dateRange: todayRange() };
    }

    this.setState(
      prev => ({
        filters: updated,
        page: 1,
        hasMore: true,
        activities: [],
        observationIds: Object.keys(prev.selected),
        selected: prev.selected
      }),
      () => this.loadMoreActivities()
    );
  }

  renderItems() {
    const { activities } = this.state;
    return activities.map(activity => (
      <Activity
        key={activity.id}
        selected={this.state.selected[activity.id]}
        onSelect={() => this.selectActivity(activity)}
        onPhotoView={this.onPhotoView}
        activity={activity}
        readOnly={this.readOnly}
      />
    ));
  }

  createActivity() {
    Actions.showModal('CreateActivity');
  }

  renderFilter() {
    const { kids } = this.props;
    const { filters } = this.state;

    const options = { '': 'All Types' };
    const icons = {};
    activityTypes.forEach(t => {
      options[t.id] = t.label;
      icons[t.id] = getActivityIconPath(t.id);
    });

    const FILTER_CONFIG = {
      activity_type: {
        type: 'select',
        title: 'All Types',
        label: 'Activity Type',
        options,
        icons
      },
      staff_only: {
        type: 'checkbox',
        label: 'Show staff only activities for approval',
        shortName: 'Staff Only'
      }
    };

    return (
      <Filter
        wide
        onSubmit={this.updateFilters}
        filters={FILTER_CONFIG}
        defaultValues={filters}
        displayApplied={!this.readOnly}
        displayDefaultDate={false}
      >
        <div className="all-activities__filters__container">
          <Form.Typeahead
            name="kid_id"
            className="input--search"
            placeholder="Search Students"
            defaultValue={filters.kid_id}
            disabled
            hideClear
            options={kids.map(k => ({ id: k.id, label: k.name }))}
          />
          <Form.DateFilter
            name="date"
            defaultValue={filters.date}
            singleValue
            disableDaily={false}
            disableBiweekly
            displayDateRange={false}
          />
        </div>
      </Filter>
    );
  }

  render() {
    const { isLoading } = this.props;
    const { hasMore, photoUrl } = this.state;

    return (
      <div className="view-activity-observations">
        <div className="modal__header modal__header--bordered">
          <div className="modal__header-title">View Activity Observations</div>
        </div>

        <div className="view-activity-observations__container">
          <div className="view-activity-observations__filters">{this.renderFilter()}</div>

          <div className="view-activity-observations__activities">
            <Scrollbars className="view-activity-observations__scroll">
              <InfiniteScroll
                pageStart={0}
                loading={isLoading}
                hasMore={hasMore}
                threshold={20}
                onEndReach={this.loadMoreActivities}
                useWindow={false}
              >
                {this.renderItems()}
                {isLoading && <Preloader center />}
              </InfiniteScroll>
            </Scrollbars>
          </div>

          {!this.readOnly && (
            <div className="help-link">
              <span className="help-link__content">
                Didn't find in the list?
                <span className="help-link__anchor" onClick={this.createActivity}>
                  CREATE NEW ACTIVITY OBSERVATION
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="modal__controls">
          <ButtonV2 onClick={this.onSubmit} label={this.readOnly ? 'Close' : 'Submit'} />
        </div>

        {photoUrl && (
          <div className="view-activity-observations__photoview" onClick={this.onPhotoViewClose}>
            <div className="view-activity-observations__photoview-wrapper">
              <img className="view-activity-observations__photoview-img" src={photoUrl} />
              <div className="view-activity-observations__photoview-icon">
                <Icon name="view" />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  kids: state.students.data,
  kidAssessmentObservations: state.kidAssessmentObservations.data,
  isLoading: state.dailyActivities.loading || state.kidAssessmentObservations.loading,
  total: state.dailyActivities.data.total
});

export default compose(withLaunchDarkly, connect(mapStateToProps))(ViewActivityObservations);
